/// <amd-module name="VSS/Features/Conduct/Components/ReportAbuse" />

import * as React from "react";

import { Dialog } from "VSSUI/Dialog";
import { FormItem } from "VSSUI/FormItem";
import { Link } from "VSSUI/Link";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { IRadioButtonProps, RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { TextField } from "VSSUI/TextField";

import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import * as ConductResources from "VSS/Features/Conduct/Resources";

import { ContentViolationCategory } from "../Contracts/ContentViolation";
import { getContentViolationClient } from "../RestClient/ContentViolation";

export interface IReportAbuseDialogProps {
    /**
     * Id of the host to report which might get taken down after the validation process (i.e. collection)
     */
    hostId: string;

    /**
     * Page context.
     */
    pageContext: IVssPageContext;

    /**
     * Delegate to be called when the dialog is closed.
     */
    dismiss: () => void;
}

export interface IReportAbuseDialogState {
    violationType?: string;
    additionalInfo?: string;
    errorMessage?: string;
    reporting: boolean;
}

export class ReportAbuseDialog extends React.Component<IReportAbuseDialogProps, IReportAbuseDialogState> {
    constructor(props: IReportAbuseDialogProps) {
        super(props);

        // Set initial state
        this.state = { additionalInfo: "", violationType: "spam", reporting: false };
    }

    public render(): JSX.Element {
        const { additionalInfo, errorMessage, reporting, violationType } = this.state;
        const canReport = violationType !== "copyright";

        return (
            <Dialog
                onDismiss={this.props.dismiss}
                calloutClassName="report-abuse"
                footerButtonProps={[
                    {
                        primary: true,
                        disabled: !canReport || reporting,
                        onClick: this.onReportClicked,
                        text: reporting
                            ? ConductResources.ReportAbuseDialogPrivacySubmittingButtonText
                            : ConductResources.ReportAbuseDialogPrivacySubmitButtonText
                    }
                ]}
                titleProps={{ text: ConductResources.ReportAbuseDialogTitle }}
            >
                {errorMessage && (
                    <MessageBar severity={MessageBarSeverity.Error} onDismiss={this.onCloseErrorMessage}>
                        {errorMessage}
                    </MessageBar>
                )}
                <FormatComponent format={ConductResources.ReportAbuseDialogSubtext} elementType="div">
                    <Link href="https://aka.ms/vstscodeofconduct">{ConductResources.ReportAbuseDialogCodeOfConductText}</Link>
                </FormatComponent>
                <RadioButtonGroup className="violation-options" selectedButtonId={violationType} onSelect={this.onViolationCategoryChanged}>
                    {this.getViolationOptions()}
                </RadioButtonGroup>
                <div className="other-container">
                    {canReport && (
                        <FormItem label={ConductResources.ReportAbuseDialogAdditionalDetailsText}>
                            <TextField multiline onChange={this.onAdditionalInfoChanged} rows={4} value={additionalInfo} />
                        </FormItem>
                    )}
                    {!canReport && (
                        <FormatComponent className="copyright-text" format={ConductResources.ReportAbuseDialogCopyrightDescription} elementType="div">
                            <Link href="https://www.microsoft.com/info/cpyrtInfrg.aspx">{ConductResources.ReportAbuseDialogCopyrightLinkText}</Link>
                        </FormatComponent>
                    )}
                </div>
                <Link href="https://privacy.microsoft.com/en-us/privacystatement">{ConductResources.ReportAbuseDialogPrivacyLinkText}</Link>
            </Dialog>
        );
    }

    private getViolationOptions(): JSX.Element[] {
        const radioProps: IRadioButtonProps[] = [
            { id: "spam", text: ConductResources.ReportAbuseDialogSpamText },
            { id: "nudity", text: ConductResources.ReportAbuseDialogNudityText },
            { id: "harassment", text: ConductResources.ReportAbuseDialogHarassmentText },
            { id: "child", text: ConductResources.ReportAbuseDialogChildText },
            { id: "copyright", text: ConductResources.ReportAbuseDialogCopyrightText },
            { id: "other", text: ConductResources.ReportAbuseDialogOtherText }
        ];

        return radioProps.map((props: IRadioButtonProps, index: number) => <RadioButton key={index} {...props} />);
    }

    private mapViolationType(violationTypeKey: string): ContentViolationCategory {
        switch (violationTypeKey) {
            case "spam":
                return ContentViolationCategory.Spam;
            case "nudity":
                return ContentViolationCategory.Nudity;
            case "harassment":
                return ContentViolationCategory.Harassment;
            case "child":
                return ContentViolationCategory.Child;
            default:
                return ContentViolationCategory.Other;
        }
    }

    private onViolationCategoryChanged = (selectedOption: string): void => {
        // Update violation category to update UI properly
        this.setState({ violationType: selectedOption });
    };

    private onAdditionalInfoChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, additionalInfo: string): void => {
        // Update additional info of the state
        this.setState({ additionalInfo });
    };

    private onCloseErrorMessage = () => {
        // Clear error message when close clicked
        this.setState({ errorMessage: undefined });
    };

    private onReportClicked = (ev: any): void => {
        // Clear any error message and disable report button and set its text to "Reporting..." until REST call finishes
        this.setState({ errorMessage: undefined, reporting: true });

        // We don't have any other way to get the project id
        const contributionService = this.props.pageContext.getService<IVssContributionService>("IVssContributionService");
        const tfsNavData = contributionService.getData<any>("ms.vss-tfs-web.page-data");

        const contentViolationService = getContentViolationClient(this.props.pageContext);
        contentViolationService
            .reportAbuse({
                hostId: this.props.hostId,
                containerId: tfsNavData && tfsNavData.project ? tfsNavData.project.id : "",
                contentUrl: window.location.href,
                additionalDetails: this.state.additionalInfo || "",
                violationCategory: this.mapViolationType(this.state.violationType!)
            })
            .then(result => {
                // Close the dialog
                if (this.props.dismiss) {
                    this.props.dismiss();
                }
            })
            .catch(error => {
                // Show error message, enable report button and reset its text
                this.setState({ errorMessage: error.message, reporting: false });
            });
    };
}
